
import { defineComponent, PropType, ref, nextTick } from "vue";
import VueDatePicker, { DatePickerMarker } from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import dayjs from "dayjs";

export default defineComponent({
  props: {
    input: {
      type: [String, Number, Date, Boolean],
      required: false,
    },
    placeholder: {
      type: String,
      required: false,
    },
    inputId: {
      type: String,
      required: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    inputType: {
      type: String as PropType<
        "basic" | "textArea" | "date" | "inline-date" | "checkbox"
      >,
      default: "basic",
    },
    dateMarkers: {
      type: Array as PropType<DatePickerMarker[]>,
      required: false,
    },
    mask: {
      type: String,
      required: false,
    },
    rows: {
      type: Number,
      default: 3,
    },
    maxRows: {
      type: Number,
      default: 10,
    },
  },
  emits: ["update:input"],
  methods: {
    getType(): string {
      if (this.inputType === "checkbox") return "checkbox";
      if (typeof this.input === "number" && !this.readOnly) return "number";
      return "text";
    },
    dateFormat(value: Date): string {
      return dayjs(value).format("DD/MM/YYYY");
    },
    autoResize(): void {
      const textarea = this.$refs.textareaRef as HTMLTextAreaElement | null;
      if (!textarea) return;

      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = "auto";

      // Calculate line height
      const computedStyle = window.getComputedStyle(textarea);
      const lineHeight = parseFloat(computedStyle.lineHeight) || 20;
      const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
      const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;

      // Calculate min and max heights
      const minHeight = lineHeight * this.rows + paddingTop + paddingBottom;
      const maxHeight = lineHeight * this.maxRows + paddingTop + paddingBottom;

      // Set the new height
      const newHeight = Math.min(
        Math.max(textarea.scrollHeight, minHeight),
        maxHeight,
      );
      textarea.style.height = `${newHeight}px`;
    },
    handleInput(event: Event): void {
      const target = event.target as HTMLTextAreaElement;
      this.$emit("update:input", target.value);
      nextTick(() => this.autoResize());
    },
  },
  data: (props) => ({
    value: ref(props.input),
  }),
  mounted() {
    if (this.inputType === "textArea") {
      nextTick(() => this.autoResize());
    }
  },
  watch: {
    input() {
      if (this.inputType === "textArea") {
        nextTick(() => this.autoResize());
      }
    },
  },
  components: {
    VueDatePicker,
  },
});
